import { t } from 'i18n';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Footer, CustomLink } from '@teko/ecommerce';
import { styled, LazyImage } from '@teko/ui';
import { NavigationBar } from './components/NavigationBar';
import { JSONObject } from 'types';
import { useTenantConfig } from 'hooks';
import { ForceLogin } from 'components/ForceLogin';
import { Topbar, TopbarButton } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { LazyComponent } from 'components/LazyComponent';
import { StringParam, useQuery } from 'hooks/useQuery';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { BannerInlinePlaceholder } from '@tekface/smart-activation';
import { INLINE_BANNER_CLASSNAME } from '../../../../constants';
import { FloatingIconList } from './components/FloatingIconList';
import { ChatbotStoreSync } from 'modules/chatbot/components/ChatbotStoreSync';
import { useChatbotStore } from 'stores/chatbotStore';

const PbLayout = dynamic(() =>
  import('modules/page-builder/layout').then((mod) => mod.PbLayout),
);

const ChatbotSidebarPanel = dynamic(
  () =>
    import('modules/chatbot/components/ChatbotSidebarPanel').then(
      (m) => m.ChatbotSidebarPanel,
    ),
  { ssr: false },
);

const TopBannerHeight = styled.div`
  height: 56px;
  @media print {
    display: none;
  }
`;

interface CustomLayoutProps extends LayoutProps {
  leftMenu?: Array<ReactNode>;
  rightMenu?: Array<ReactNode>;
  footer?: React.ReactNode;
  navigationBar?: React.ReactNode;
  forceLogin?: boolean;
}

const OUTER_FLEX_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
};
const CONTENT_FLEX_STYLE: React.CSSProperties = { flex: 1, minWidth: 0 };

// Wrapper thêm chatbot sidebar vào layout. Khi chatbot không được bật (isEnabled = false —
// tức là tenant không có chatbotConfig hoặc user không phải teko), render layout cũ.
function ChatbotPageContent({
  children,
  footer,
  isChatbotPage,
}: {
  children: React.ReactNode;
  footer: React.ReactNode;
  isChatbotPage: boolean;
}) {
  const { open, isEnabled, sidebarWidth } = useChatbotStore();
  const sidebarStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'sticky',
      top: 'var(--chatbot-nav-height, 72px)',
      height: 'calc(100vh - var(--chatbot-total-offset, 72px))',
      width: open ? sidebarWidth : 0,
      flexShrink: 0,
      overflow: 'hidden',
      transition: 'width 0.3s ease',
      zIndex: 1039,
    }),
    [open, sidebarWidth],
  );
  const sidebarInnerStyle = useMemo<React.CSSProperties>(
    () => ({ width: sidebarWidth, height: '100%' }),
    [sidebarWidth],
  );

  // Luồng cũ — không thay đổi gì so với trước khi có chatbot
  if (!isEnabled) {
    return (
      <>
        <div>{children}</div>
        {!isChatbotPage && <LazyComponent>{footer}</LazyComponent>}
      </>
    );
  }

  return (
    <div style={OUTER_FLEX_STYLE}>
      <div style={sidebarStyle}>
        {/* Always mounted to preserve AssistantPanel state across open/close */}
        <div style={sidebarInnerStyle}>
          <ChatbotSidebarPanel />
        </div>
      </div>
      {/* minWidth: 0 prevents flex child from overflowing its container */}
      <div style={CONTENT_FLEX_STYLE}>
        <div>{children}</div>
        {!isChatbotPage && <LazyComponent>{footer}</LazyComponent>}
      </div>
    </div>
  );
}

const DefaultLayout: React.FC<CustomLayoutProps> = ({
  children,
  banners,
  leftMenu,
  rightMenu,
  footerData,
  footer,
  navigationBar,
  forceLogin = false,
  layoutConfig,
  pageProps,
  inlineBanners,
}: CustomLayoutProps) => {
  const LoginWrapper = forceLogin ? ForceLogin : React.Fragment;
  const { partner, footerDataConfig, noOverride, epicPb1887 } =
    useTenantConfig();
  const { pathname } = useRouter();
  const isChatbotPage = pathname.endsWith('/chatbot');
  const topBanner = banners?.[0];

  if (epicPb1887) {
    return (
      <>
        <PbLayout config={layoutConfig || {}} pageProps={pageProps}>
          <LoginWrapper>
            {children}
            <FloatingIconList />
          </LoginWrapper>
        </PbLayout>
        {/* Không làm gì nếu tenant không có chatbotConfig */}
        <ChatbotStoreSync />
      </>
    );
  }

  if ((!footer && footerData) || noOverride) {
    const footerLayout = noOverride
      ? {
          ...footerData,
          supportAndPolicy: [
            ...(footerDataConfig?.['supportAndPolicy' as keyof JSONObject] ??
              []),
          ],
        }
      : footerData;
    footer = <Footer footer={footerLayout ?? {}} />;
  }
  const renderContent = () => {
    return (
      <>
        {!partner && (
          <>
            {topBanner && (
              <TopBannerHeight
                {...getTrackingData({
                  regionName: 'topBanner',
                  isTrackContent: true,
                })}
              >
                <CustomLink targetPath={topBanner.targetPath}>
                  <LazyImage
                    imageWidth={1920}
                    height={56}
                    cover
                    src={topBanner.image}
                  />
                </CustomLink>
              </TopBannerHeight>
            )}
            <BannerInlinePlaceholder
              banners={inlineBanners}
              className={INLINE_BANNER_CLASSNAME.INLINE_HOME_TOP}
            />
            {(leftMenu || rightMenu) && (
              <Topbar leftMenu={leftMenu} rightMenu={rightMenu} />
            )}
          </>
        )}
        {navigationBar}
        <ChatbotPageContent isChatbotPage={isChatbotPage} footer={footer}>
          {children}
        </ChatbotPageContent>
      </>
    );
  };

  return (
    <>
      <LoginWrapper>{renderContent()}</LoginWrapper>
      {/* Không làm gì nếu tenant không có chatbotConfig */}
      <ChatbotStoreSync />
      <FloatingIconList />
    </>
  );
};
export const Layout: React.FunctionComponent<CustomLayoutProps> = ({
  children,
  banners,
  leftMenu,
  rightMenu,
  footerData,
  footer,
  navigationBar,
  menuItems,
  liteLogo,
  forceLogin = false,
  layoutAreas,
  ...props
}) => {
  const { identityRequired, hasNews } = useTenantConfig();
  const [requireLogin, setRequireLogin] = useState(forceLogin);
  const finalRightActions: Array<ReactNode> = hasNews
    ? [
        <TopbarButton key={0} href="/tin-tuc">
          {t('Tin tức')}
        </TopbarButton>,
        ...(rightMenu ?? []),
      ]
    : rightMenu ?? [];
  const query = useQuery({ ref_code: StringParam });

  useEffect(() => {
    if (identityRequired && query?.ref_code) {
      sessionStorage.setItem('ref_code', query.ref_code.toString());
      setRequireLogin(true);
    } else if (!!sessionStorage.getItem('ref_code')) {
      // for case redirected back after login
      setRequireLogin(true);
    }
  }, []);

  if (!navigationBar) {
    navigationBar = (
      <NavigationBar
        menuItems={menuItems}
        showShippingFeeMiniCart={false}
        liteLogo={liteLogo}
      />
    );
  }
  return (
    <DefaultLayout
      leftMenu={leftMenu}
      rightMenu={finalRightActions}
      navigationBar={navigationBar}
      footer={footer}
      footerData={footerData}
      banners={banners}
      forceLogin={requireLogin}
      layoutAreas={layoutAreas}
      {...props}
    >
      {children}
    </DefaultLayout>
  );
};
